import { formatMoney } from '@expenditure/core';
import { useState, type ReactNode } from 'react';
import { useAuth, useCurrentUser } from '../auth/AuthProvider';
import { useToast } from '../components/Toast';
import { api } from '../lib/api';
import { isInTelegram, requestBotMessages } from '../lib/telegram';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { CategoryIcon, EmptyState, ErrorState, HeroAmount, PageHeader, PageSkeleton, SectionTitle } from '../components/ui';
import { errorMessage } from '../lib/format';
import { useStats } from '../lib/queries';
import { Icon } from '../components/Icon';

/**
 * Where the money went. Each breakdown is a ranked table with an inline bar:
 * one series, so one colour for every bar (length carries the value); every
 * value is printed as text, so the table *is* the accessible view and
 * nothing depends on hover, which phones don't have.
 */
export function InsightsPage() {
  const { groupId = '' } = useParams();
  const { t } = useTranslation();
  const { data, isPending, error, refetch } = useStats(groupId);

  if (isPending) return <PageSkeleton />;
  if (error) return <ErrorState message={errorMessage(error, t)} onRetry={() => void refetch()} />;

  const { currency, totalSpentMinor, byCategory, byMember } = data;
  const percent = (minor: number) => (totalSpentMinor > 0 ? Math.round((minor / totalSpentMinor) * 100) : 0);

  return (
    <main className="page page--no-tabbar app">
      <PageHeader title={t('insights.title')} />
      <div className="hero">
        <div className="hero-label">{t('common.total')}</div>
        <HeroAmount minor={totalSpentMinor} currency={currency} />
      </div>

      {byCategory.length === 0 ? (
        <div className="card">
          <EmptyState icon="chart" title={t('group.noExpenses')} />
        </div>
      ) : (
        <>
          <SectionTitle>{t('insights.byCategory')}</SectionTitle>
          <BarTable
            label={t('insights.byCategory')}
            headers={[t('insights.category'), t('insights.amount'), t('insights.percent')]}
            max={byCategory[0]!.amountMinor}
            rows={byCategory.map((c) => ({
              key: c.category,
              name: (
                <>
                  <span className="row" style={{ gap: 8 }}>
                    <CategoryIcon category={c.category} small />
                    {t(`categories.${c.category}`)}
                  </span>
                </>
              ),
              detail: t('insights.expenses', { count: c.expenseCount }),
              value: c.amountMinor,
              display: formatMoney(c.amountMinor, currency),
              percent: percent(c.amountMinor),
            }))}
          />

          <SectionTitle>{t('insights.byPerson')}</SectionTitle>
          <p className="muted small" style={{ margin: '-4px 4px 0' }}>
            {t('insights.byPersonHint')}
          </p>
          <BarTable
            label={t('insights.byPerson')}
            headers={[t('insights.person'), t('insights.amount'), t('insights.percent')]}
            max={Math.max(1, ...byMember.map((m) => m.shareMinor))}
            rows={byMember.map((m) => ({
              key: m.memberId,
              name: m.displayName,
              detail: t('insights.paid', { amount: formatMoney(m.paidMinor, currency) }),
              value: m.shareMinor,
              display: formatMoney(m.shareMinor, currency),
              percent: percent(m.shareMinor),
            }))}
          />
        </>
      )}

      <ExportButton groupId={groupId} />
    </main>
  );
}

/**
 * Inside Telegram the bot sends the CSV to the user's chat (Mini Apps can't
 * reliably download files); in a normal browser it downloads directly.
 */
function ExportButton({ groupId }: { groupId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useCurrentUser();
  const { setUser } = useAuth();
  const [busy, setBusy] = useState(false);

  const viaBrowser = async () => {
    const url = URL.createObjectURL(await api.exportCsv(groupId));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'expenditure.csv';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  const viaTelegram = async () => {
    if (!user.botAllowed) {
      if (!(await requestBotMessages())) {
        toast(t('profile.permissionDenied'), 'error');
        return;
      }
      setUser((await api.updateMe({ botAllowed: true })).user);
    }
    const result = await api.exportToTelegram(groupId);
    if (result.delivered) toast(t('insights.exportSent'));
    else toast(t('insights.exportFailed'), 'error');
  };

  return (
    <button
      className="btn btn--soft btn--block"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await (isInTelegram() ? viaTelegram() : viaBrowser());
        } catch (error) {
          toast(errorMessage(error, t), 'error');
        } finally {
          setBusy(false);
        }
      }}
    >
      <Icon name="download" size={18} />
      {t('insights.export')}
    </button>
  );
}

interface BarRow {
  key: string;
  name: ReactNode;
  detail: string;
  value: number;
  display: string;
  percent: number;
}

function BarTable({ label, headers, rows, max }: { label: string; headers: [string, string, string]; rows: BarRow[]; max: number }) {
  return (
    <div className="card card--flush">
      <table className="bar-table" aria-label={label}>
        <thead className="visually-hidden">
          <tr>
            <th scope="col">{headers[0]}</th>
            <th scope="col">{headers[1]}</th>
            <th scope="col">{headers[2]}</th>
          </tr>
        </thead>
        {rows.map((row) => (
          // One tbody per item: the data row, then its full-width bar.
          <tbody key={row.key}>
            <tr>
              <th scope="row">
                <div className="bar-table-name ellipsis">{row.name}</div>
                <div className="list-sub">{row.detail}</div>
              </th>
              <td className="num">{row.display}</td>
              <td className="num muted">{row.percent}%</td>
            </tr>
            <tr aria-hidden="true">
              <td colSpan={3} className="bar-cell">
                <div className="bar-track">
                  <span className="bar-fill" style={{ width: `${Math.max(2, (row.value / max) * 100)}%` }} />
                </div>
              </td>
            </tr>
          </tbody>
        ))}
      </table>
    </div>
  );
}
